import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent
} from "react";
import {
  ArrowDown,
  ArrowUp,
  Check,
  Lightbulb
} from "lucide-react";

import type { ActivityExecutionContext } from "@/app/integration/contracts";
import { useCurriculumFeedbackAudio } from "@/features/audio";
import {
  CurriculumVoiceButton,
  IconButton,
  PrimaryCommandButton
} from "@/ui";

import {
  HabitsFeedbackNotice,
  HabitsInstructionHeader,
  HabitsResult,
  HabitsSceneImage
} from "./PlayerChrome";
import type { DailyRoutinePlayerProps } from "./player-contracts";
import {
  useCompletionReporting,
  usePlayerFocus
} from "./player-lifecycle";
import type { DailyRoutineAction } from "./types";

export function DailyRoutinePlayer({
  audioPlayer,
  binding,
  onAudioReplay,
  onComplete,
  onReplay,
  reducedMotion
}: DailyRoutinePlayerProps) {
  const titleId = useId();
  const instructionId = useId();
  const listTitleId = useId();
  const context = useMemo<ActivityExecutionContext>(
    () => ({
      nowMs: Date.now,
      reducedMotion
    }),
    [reducedMotion]
  );
  const payload = binding.level.payload;
  const [state, setState] = useState(() =>
    binding.adapter.createInitialState(payload, context)
  );
  const firstControlRef = useRef<HTMLButtonElement>(null);
  const resultHeadingRef = useRef<HTMLHeadingElement>(null);

  const dispatch = useCallback(
    (action: DailyRoutineAction) => {
      setState((currentState) =>
        binding.adapter.reduce(
          currentState,
          payload,
          action,
          context
        )
      );
    },
    [binding, context, payload]
  );

  const model = useMemo(
    () => binding.adapter.toRenderModel(state, payload),
    [binding, payload, state]
  );
  const metrics = useMemo(
    () => binding.adapter.getMetrics(state, payload),
    [binding, payload, state]
  );

  const feedbackAudio = useCurriculumFeedbackAudio(
    audioPlayer,
    model.feedback,
    model.phase === "play"
  );

  useCompletionReporting(metrics, onComplete);
  usePlayerFocus(
    metrics.completed,
    firstControlRef,
    resultHeadingRef
  );

  const handleStepKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, itemId: string) => {
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        dispatch({
          type: "move-item",
          itemId,
          direction: event.key === "ArrowUp" ? "backward" : "forward",
          inputMethod: "keyboard"
        });
        return;
      }

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        dispatch({
          type: "focus-item",
          itemId,
          inputMethod: "keyboard"
        });
      }
    },
    [dispatch]
  );

  const handleReplay = useCallback(() => {
    dispatch({ type: "replay" });
    onReplay?.(binding.level.id);
  }, [binding.level.id, dispatch, onReplay]);

  return (
    <section
      aria-labelledby={titleId}
      className="habits-player habits-player--routine"
      data-activity-type={binding.activityType}
      data-reduced-motion={reducedMotion}
    >
      <HabitsInstructionHeader
        difficulty={model.difficulty}
        instruction={model.instruction}
        instructionId={instructionId}
        instructionPlaying={audioPlayer.isPlayingPath(
          model.instruction.audioPath
        )}
        levelId={model.levelId}
        onAudioReplay={onAudioReplay}
        title={model.title}
        titleId={titleId}
      />
      <HabitsSceneImage src={model.sceneImagePath} />

      {model.phase === "result" && model.result !== null ? (
        <HabitsResult
          description="每一步都回到了该去的位置。"
          metrics={model.result}
          onReplay={handleReplay}
          resultHeadingRef={resultHeadingRef}
        />
      ) : (
        <>
          <section
            aria-describedby={instructionId}
            aria-labelledby={listTitleId}
            className="habits-player__work-area"
          >
            <h3 id={listTitleId}>按先后顺序排一排</h3>
            <ol className="habits-routine__list">
              {model.items.map((item, index) => (
                <li
                  className="habits-routine__item"
                  data-hinted={item.hinted}
                  key={item.id}
                >
                  <span
                    aria-hidden="true"
                    className="habits-routine__position"
                  >
                    {item.position}
                  </span>
                  <button
                    aria-label={
                      "第 " + item.position + " 步：" + item.label
                    }
                    aria-pressed={item.focused}
                    className="habits-routine__step"
                    onClick={() =>
                      dispatch({
                        type: "focus-item",
                        itemId: item.id,
                        inputMethod: "pointer"
                      })
                    }
                    onFocus={() =>
                      dispatch({
                        type: "focus-item",
                        itemId: item.id,
                        inputMethod: "keyboard"
                      })
                    }
                    onKeyDown={(event) =>
                      handleStepKeyDown(event, item.id)
                    }
                    ref={index === 0 ? firstControlRef : undefined}
                    type="button"
                  >
                    {item.label}
                  </button>
                  <CurriculumVoiceButton
                    isPlaying={audioPlayer.isPlayingPath(item.audioPath)}
                    label={item.label}
                    onPlay={() =>
                      onAudioReplay({
                        audioPath: item.audioPath,
                        caption: item.label,
                        kind: "item",
                        levelId: model.levelId
                      })
                    }
                  />
                  <div
                    aria-label={item.label + "的位置"}
                    className="habits-routine__move-controls"
                    role="group"
                  >
                    <IconButton
                      disabled={!item.canMoveEarlier}
                      icon={ArrowUp}
                      label={"把" + item.label + "向前移动"}
                      onClick={() =>
                        dispatch({
                          type: "move-item",
                          itemId: item.id,
                          direction: "backward",
                          inputMethod: "pointer"
                        })
                      }
                      tooltip="向前移动"
                    />
                    <IconButton
                      disabled={!item.canMoveLater}
                      icon={ArrowDown}
                      label={"把" + item.label + "向后移动"}
                      onClick={() =>
                        dispatch({
                          type: "move-item",
                          itemId: item.id,
                          direction: "forward",
                          inputMethod: "pointer"
                        })
                      }
                      tooltip="向后移动"
                    />
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <HabitsFeedbackNotice
            feedback={model.feedback}
            isPlaying={feedbackAudio.isPlaying}
            onReplay={() => {
              if (model.feedback !== null) {
                onAudioReplay({
                  audioPath: model.feedback.audioPath,
                  caption: model.feedback.text,
                  kind: "feedback",
                  levelId: model.levelId
                });
              }
            }}
          />

          <div className="habits-player__actions">
            <PrimaryCommandButton
              icon={Lightbulb}
              onClick={() => dispatch({ type: "request-hint" })}
            >
              给我提示
            </PrimaryCommandButton>
            <PrimaryCommandButton
              icon={Check}
              onClick={() => dispatch({ type: "check-order" })}
            >
              检查顺序
            </PrimaryCommandButton>
          </div>
        </>
      )}
    </section>
  );
}
